import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MediaOutletDialog } from './MediaOutletDialog';
import { mediaOutletService } from '@/services/mediaOutletService';
import type { MediaOutlet } from '@/types/mediaOutlets';

export function MediaOutletManager() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState<MediaOutlet | null>(null);

  const { data: outlets = [], isLoading } = useQuery({
    queryKey: ['media-outlets', searchTerm],
    queryFn: () => searchTerm 
      ? mediaOutletService.searchOutlets(searchTerm)
      : mediaOutletService.getAllOutlets(),
  });

  const deleteMutation = useMutation({
    mutationFn: mediaOutletService.deleteOutlet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-outlets'] });
      toast.success('Media outlet deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete media outlet');
    },
  });

  const handleCreate = () => {
    setEditingOutlet(null);
    setDialogOpen(true);
  };

  const handleEdit = (outlet: MediaOutlet) => {
    setEditingOutlet(outlet);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this media outlet?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingOutlet(null);
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search media outlets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Outlet
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading media outlets...
          </div>
        ) : outlets.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm ? 'No outlets found matching your search' : 'No media outlets yet'}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Popular</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Usage Count</TableHead>
                  <TableHead>Display Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {outlets.map((outlet) => (
                  <TableRow key={outlet.id}>
                    <TableCell className="font-medium">{outlet.outlet_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{outlet.outlet_category}</Badge>
                    </TableCell>
                    <TableCell>
                      {outlet.is_popular && <Badge>Popular</Badge>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={outlet.is_active ? 'default' : 'secondary'}>
                        {outlet.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>{outlet.usage_count || 0}</TableCell>
                    <TableCell>{outlet.display_order}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(outlet)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(outlet.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <MediaOutletDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        outlet={editingOutlet}
      />
    </div>
  );
}
